import React, { useState, useContext, forwardRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Grid, Button } from '@flatfy/supernova-new';
import IconFilter from '../../icons/filters.svg';
import {
  FlatsFiltersContentContext,
  FlatsFiltersOpenedContext,
} from '../../FlatsFilters/FlatsFiltersContext';
import FlatsFilters from '../../FlatsFilters/FlatsFilters';
import FlatsSwitcher from '../FlatsSwitcher/FlatsSwitcher';
import PriceRange from './PriceRange/PriceRange';
import { attachGaDataAttributes } from '../../helpers/analytics';
import useMediaQuery from '../../FlatsFilters/useMediaQuery';

const EVENT = {
  category: window.eventCategory,
  action: 'filters',
};
const CLASS_POPUP_NO_SCROLL = 'UIPopup-no-scroll';
const CLASS_MENU_NO_SCROLL = 'FlatsMenu-no-scroll';

const FlatsFilter = forwardRef(({ requestUrl, withDivider }, menuRef) => {
  const [opened, setOpened] = useContext(FlatsFiltersOpenedContext);
  const [filterParams, setFilterParams] = useState({});
  const { searchParams, isMapVisible, activeFiltersCount } = useContext(FlatsFiltersContentContext);
  const [triggerEl, setTriggerEl] = useState(null);
  const openedPopup = Boolean(triggerEl);
  const body = document.querySelector('body');
  const isMobile = useMediaQuery();

  const onOpenedChange = (isOpened) => {
    if (isOpened) {
      menuRef.current.classList.add(CLASS_MENU_NO_SCROLL);
    } else {
      menuRef.current.classList.remove(CLASS_MENU_NO_SCROLL);
    }
  };

  const openFilters = () => {
    body.classList.add(CLASS_POPUP_NO_SCROLL);
    onOpenedChange(true);
    setOpened(true);
  };

  const closeFilters = () => {
    if (!opened) return;
    body.classList.remove(CLASS_POPUP_NO_SCROLL);
    onOpenedChange(false);
    setOpened(false);
  };

  const handleClick = (event) => {
    event.preventDefault();
    openFilters();
    setTriggerEl(triggerEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    closeFilters();
    setTriggerEl(null);
  };

  return (
    <div className={cx('FlatsFilter', { '-divider': withDivider })}>
      <Grid.Container spaceBetween={isMobile ? 8 : 20}>
        {filterParams.ready_state && (
          <Grid.Item col={6} className="FlatsFilter-select">
            <FlatsFilters.Select
              filterParams={{ readyState: filterParams.ready_state }}
              selectRoot={!isMapVisible ? menuRef.current : null}
              onOpenedChange={onOpenedChange}
            />
          </Grid.Item>
        )}
        <Grid.Item col={6} md="56px" sm="56px" className="FlatsFilter-button">
          <Button
            classes={{
              root: cx('FlatsFilter-trigger', { '-active': openedPopup }),
              leftIcon: 'FlatsFilter-trigger-icon',
              content: 'FlatsFilter-trigger-content',
            }}
            variant="secondary-outline"
            onClick={handleClick}
            leftIcon={<IconFilter />}
            fullWidth={!isMobile}
            {...attachGaDataAttributes({
              ...EVENT,
              label: 'open_click',
            })}
          >
            {!isMobile && <FormattedMessage id="filters" tagName="div" />}
            {activeFiltersCount !== 0 && <span>{activeFiltersCount}</span>}
          </Button>
        </Grid.Item>
        <Grid.Item md="56px" sm="56px" className="FlatsFilter-button FlatsFilter-switcher">
          <FlatsSwitcher />
        </Grid.Item>
      </Grid.Container>
      <FlatsFilters
        requestUrl={requestUrl}
        requestParams={{
          building_id: window.params.buildingId,
          lang: window.currentLanguage,
        }}
        onResponse={(response) => setFilterParams(response.data)}
        skipKeys={['building_id', 'flats']}
        triggerEl={triggerEl}
        onClose={() => {
          setTriggerEl(null);
          onOpenedChange(false);
        }}
      >
        {!!(filterParams.price || filterParams.rooms_count) && !isMobile && (
          <Grid.Container spaceBetween={20}>
            {!!filterParams.price && (
              <Grid.Item col={6} md={12}>
                <PriceRange filterParams={filterParams} searchParams={searchParams} />
              </Grid.Item>
            )}
            {filterParams.rooms_count && (
              <Grid.Item col={6} md={12}>
                <FlatsFilters.Section filterParams={filterParams.rooms_count} />
              </Grid.Item>
            )}
          </Grid.Container>
        )}
        <FlatsFilters.Popup
          opened={openedPopup}
          triggerEl={triggerEl}
          onClose={handleClose}
          skipKeys={['building_id', 'flats']}
          root={menuRef.current}
        >
          <>
            {!!filterParams.price && isMobile && (
              <PriceRange filterParams={filterParams} searchParams={searchParams} />
            )}
            {!!filterParams.abs_min_area && (
              <FlatsFilters.Range
                type="area"
                event={{
                  ...EVENT,
                  label: 'area_click',
                }}
                rangeMin={filterParams.abs_min_area}
                rangeMax={filterParams.abs_max_area}
                minValue={
                  searchParams.min_area ? Number(searchParams.min_area) : filterParams.abs_min_area
                }
                maxValue={
                  searchParams.max_area ? Number(searchParams.max_area) : filterParams.abs_max_area
                }
              />
            )}
            {!!filterParams.abs_min_floor && (
              <FlatsFilters.Range
                type="floor"
                event={{
                  ...EVENT,
                  label: 'floor_click',
                }}
                rangeMin={filterParams.abs_min_floor}
                rangeMax={filterParams.abs_max_floor}
                minValue={
                  searchParams.min_floor
                    ? Number(searchParams.min_floor)
                    : filterParams.abs_min_floor
                }
                maxValue={
                  searchParams.max_floor
                    ? Number(searchParams.max_floor)
                    : filterParams.abs_max_floor
                }
              />
            )}
            {filterParams.rooms_count && isMobile && (
              <FlatsFilters.Section filterParams={filterParams.rooms_count} />
            )}
            {filterParams.features && <FlatsFilters.Section filterParams={filterParams.features} />}
            {filterParams.show_sold && (
              <FlatsFilters.Switch filterParams={filterParams.show_sold} />
            )}
          </>
        </FlatsFilters.Popup>
      </FlatsFilters>
    </div>
  );
});

FlatsFilter.propTypes = {
  requestUrl: PropTypes.string.isRequired,
  withDivider: PropTypes.bool,
};

export default FlatsFilter;
